import type { CargaTrabalhoAlocacao, ConflitoAlocacao } from "@/modules/carga-trabalho";

/**
 * Conflito do fornecedor (HU 2041, RN01): o conjunto de alocações ligadas por sobreposição em
 * obras distintas. Com três ou mais obras envolvidas, todas fazem parte do mesmo conflito.
 */
export interface ConflitoFornecedor {
  /** Alocações envolvidas, em ordem de início. */
  alocacoes: CargaTrabalhoAlocacao[];
  /** Do primeiro ao último dia de sobreposição entre elas (`yyyy-MM-dd`). */
  inicio: string;
  fim: string;
  alocacaoIds: string[];
}

/** Só a sobreposição entre obras distintas é conflito de fornecedor. */
export function isConflitoEntreObras(conflito: ConflitoAlocacao): boolean {
  return conflito.a.obra.id !== conflito.b.obra.id;
}

/** Une os pares sobrepostos em conflitos (componentes conexos), sem limitar a duas obras. */
export function agruparConflitos(pares: ConflitoAlocacao[]): ConflitoFornecedor[] {
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    const root = parent.get(id) ?? id;
    if (root === id) return id;
    const top = find(root);
    parent.set(id, top);
    return top;
  };

  const alocacoes = new Map<string, CargaTrabalhoAlocacao>();
  for (const { a, b } of pares) {
    alocacoes.set(a.id, a);
    alocacoes.set(b.id, b);
    parent.set(find(a.id), find(b.id));
  }

  const grupos = new Map<string, ConflitoAlocacao[]>();
  for (const par of pares) {
    const root = find(par.a.id);
    grupos.set(root, [...(grupos.get(root) ?? []), par]);
  }

  return [...grupos.values()]
    .map((items) => {
      const ids = new Set(items.flatMap(({ a, b }) => [a.id, b.id]));
      const envolvidas = [...ids]
        .map((id) => alocacoes.get(id))
        .filter((item): item is CargaTrabalhoAlocacao => item !== undefined)
        .sort((x, y) => x.inicio.localeCompare(y.inicio) || x.id.localeCompare(y.id));
      return {
        alocacoes: envolvidas,
        inicio: items.map((item) => item.inicio).sort()[0] ?? "",
        fim:
          items
            .map((item) => item.fim)
            .sort()
            .at(-1) ?? "",
        alocacaoIds: envolvidas.map((item) => item.id),
      };
    })
    .sort((x, y) => x.inicio.localeCompare(y.inicio));
}

/** Quantidade de obras distintas no conflito. */
export function contarObras(conflito: ConflitoFornecedor): number {
  return new Set(conflito.alocacoes.map((alocacao) => alocacao.obra.id)).size;
}
